import { useState, useEffect, useCallback, useRef } from 'react';
import { casesApi, QuickStats } from '../api/cases';

const POLL_INTERVAL = 5000; // 5 seconds - processing status changes frequently

interface UseQuickStatsReturn {
  stats: QuickStats | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useQuickStats(): UseQuickStatsReturn {
  const [stats, setStats] = useState<QuickStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      const data = await casesApi.getQuickStats();
      setStats(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch quick stats:', err);
      setError('Failed to fetch quick stats');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Initial fetch
    fetchStats();

    // Set up polling
    intervalRef.current = setInterval(fetchStats, POLL_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchStats]);

  return {
    stats,
    isLoading,
    error,
    refresh: fetchStats,
  };
}
